// ─── Timetable wizard step definitions ────────────────────────────────────

export const TT_NORMAL_STEPS = [
  {
    key: 'name',
    label: 'Enter timetable name:',
    hint: 'A unique label for this timetable (e.g. "Morning 9-5").',
  },
  {
    key: 'check_in',
    label: 'Check-In time (HH:MM):',
    hint: 'Expected clock-in time (e.g. 09:00).',
  },
  {
    key: 'check_out',
    label: 'Check-Out time (HH:MM):',
    hint: 'Expected clock-out time (e.g. 17:00).',
  },
  {
    key: 'flexi_cross',
    label: 'Cross days for Check-Out (0, 1, 2, or 3):',
    hint: '0 = same day. Use 1 if the shift ends after midnight.',
  },
  {
    key: 'ci_start',
    label: 'Check-In Start time (HH:MM), or type "skip":',
    hint: 'Earliest time an employee can clock in. Type "skip" for no restriction.',
  },
  {
    key: 'ci_start_cross',
    label: 'Cross days for Check-In Start (0–3):',
    hint: '0 = same day. Usually 0 unless the window crosses midnight.',
    skipIf: (d) => !d.ci_start || d.ci_start === 'skip',
  },
  {
    key: 'co_start',
    label: 'Check-Out Start time (HH:MM), or type "skip":',
    hint: 'Earliest time an employee can clock out. Type "skip" for no restriction.',
  },
  {
    key: 'co_start_cross',
    label: 'Cross days for Check-Out Start (0–3):',
    hint: '0 = same day. Usually 0 unless the window crosses midnight.',
    skipIf: (d) => !d.co_start || d.co_start === 'skip',
  },
  {
    key: 'ci_end',
    label: 'Check-In End time (HH:MM):',
    hint: 'Latest allowed clock-in time (e.g. 10:00).',
  },
  {
    key: 'ci_end_cross',
    label: 'Cross days for Check-In End (0–3):',
    hint: '0 = same day. Use 1 if the window crosses midnight.',
  },
  {
    key: 'co_end',
    label: 'Check-Out End time (HH:MM):',
    hint: 'Latest allowed clock-out time (e.g. 20:00).',
  },
  {
    key: 'co_end_cross',
    label: 'Cross days for Check-Out End (0–3):',
    hint: '0 = same day. Use 1 if the window crosses midnight.',
  },
  // work_duration is always skipped — defaults to 480 min (8 h)
  { key: 'work_duration', label: '', skipIf: () => true },
  {
    key: 'ot_confirm',
    label: 'Add overtime rules?',
    hint: 'Enable if employees earn overtime pay for extra hours worked.',
    options: [
      { label: 'Yes, add OT rules', value: 'yes' },
      { label: 'No overtime', value: 'no' },
    ],
  },
  {
    key: 'ot_max_confirm',
    label: 'Enable Max OT limit?',
    hint: 'Cap the maximum overtime hours that can be earned per day.',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    skipIf: (d) => d.ot_confirm !== 'yes',
  },
  {
    key: 'ot_max_dur',
    label: 'Max OT duration in minutes:',
    hint: 'e.g. 120 = max 2 hours of overtime per day.',
    skipIf: (d) => d.ot_max_confirm !== 'yes',
  },
  {
    key: 'ot_l1',
    label: 'Level 1 OT \u2014 work-hour range (e.g. "8 10"):',
    hint: 'Enter start and end work-hour for L1 OT. For an 8-hr shift, "8 10" means OT L1 covers hours 8\u201310.',
    skipIf: (d) => d.ot_confirm !== 'yes',
  },
  {
    key: 'ot_l2_confirm',
    label: 'Add Level 2 OT?',
    hint: 'A second overtime tier with a different rate.',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    skipIf: (d) => d.ot_confirm !== 'yes',
  },
  {
    key: 'ot_l2',
    label: 'Level 2 OT \u2014 work-hour range (e.g. "10 12"):',
    hint: '"10 12" means OT L2 covers hours 10\u201312 of the workday.',
    skipIf: (d) => d.ot_l2_confirm !== 'yes',
  },
  {
    key: 'ot_l3_confirm',
    label: 'Add Level 3 OT?',
    hint: 'A third overtime tier (highest rate).',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    skipIf: (d) => d.ot_confirm !== 'yes',
  },
  {
    key: 'ot_l3',
    label: 'Level 3 OT \u2014 work-hour range (e.g. "12 14"):',
    hint: '"12 14" means OT L3 covers hours 12\u201314 of the workday.',
    skipIf: (d) => d.ot_l3_confirm !== 'yes',
  },
  {
    key: 'late_in',
    label: 'Allow Late-In in minutes (0 to disable):',
    hint: 'Grace period before an employee is marked late. 0 = no grace.',
  },
  {
    key: 'early_out',
    label: 'Allow Early-Out in minutes (0 to disable):',
    hint: 'Grace period before an early departure is flagged. 0 = no grace.',
  },
]

export const TT_FLEXIBLE_STEPS = [
  {
    key: 'name',
    label: 'Enter timetable name:',
    hint: 'A unique label for this timetable (e.g. "Flexi Core-Hours").',
  },
  {
    key: 'check_in',
    label: 'Check-In time (HH:MM):',
    hint: 'Typical start time for flexible shifts (e.g. 09:00).',
  },
  {
    key: 'check_out',
    label: 'Check-Out time (HH:MM):',
    hint: 'Typical end time for flexible shifts (e.g. 17:00).',
  },
  {
    key: 'flexi_cross',
    label: 'Cross days (0, 1, 2, or 3):',
    hint: '0 = same day. Use 1 if the shift can end after midnight.',
  },
  // work_duration is always skipped — defaults to 480 min (8 h)
  { key: 'work_duration', label: '', skipIf: () => true },
  {
    key: 'ot_confirm',
    label: 'Add overtime rules?',
    hint: 'Enable if employees earn overtime pay for extra hours worked.',
    options: [
      { label: 'Yes, add OT rules', value: 'yes' },
      { label: 'No overtime', value: 'no' },
    ],
  },
  {
    key: 'ot_max_confirm',
    label: 'Enable Max OT limit?',
    hint: 'Cap the maximum overtime hours that can be earned per day.',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    skipIf: (d) => d.ot_confirm !== 'yes',
  },
  {
    key: 'ot_max_dur',
    label: 'Max OT duration in minutes:',
    hint: 'e.g. 120 = max 2 hours of overtime per day.',
    skipIf: (d) => d.ot_max_confirm !== 'yes',
  },
  {
    key: 'ot_l1',
    label: 'Level 1 OT \u2014 work-hour range (e.g. "8 10"):',
    hint: 'Enter start and end work-hour for L1 OT. For an 8-hr shift, "8 10" means OT L1 covers hours 8\u201310.',
    skipIf: (d) => d.ot_confirm !== 'yes',
  },
  {
    key: 'ot_l2_confirm',
    label: 'Add Level 2 OT?',
    hint: 'A second overtime tier with a different rate.',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    skipIf: (d) => d.ot_confirm !== 'yes',
  },
  {
    key: 'ot_l2',
    label: 'Level 2 OT \u2014 work-hour range (e.g. "10 12"):',
    hint: '"10 12" means OT L2 covers hours 10\u201312 of the workday.',
    skipIf: (d) => d.ot_l2_confirm !== 'yes',
  },
  {
    key: 'ot_l3_confirm',
    label: 'Add Level 3 OT?',
    hint: 'A third overtime tier (highest rate).',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    skipIf: (d) => d.ot_confirm !== 'yes',
  },
  {
    key: 'ot_l3',
    label: 'Level 3 OT \u2014 work-hour range (e.g. "12 14"):',
    hint: '"12 14" means OT L3 covers hours 12\u201314 of the workday.',
    skipIf: (d) => d.ot_l3_confirm !== 'yes',
  },
  {
    key: 'late_in',
    label: 'Allow Late-In in minutes (0 to disable):',
    hint: 'Grace period before an employee is marked late. 0 = no grace.',
  },
  {
    key: 'early_out',
    label: 'Allow Early-Out in minutes (0 to disable):',
    hint: 'Grace period before an early departure is flagged. 0 = no grace.',
  },
]
